import APP from 'src/main.js'
import AppRecord from './AppRecord.js'
import { useEmployeeStore } from 'stores/employeeStore'
const employeeStore = useEmployeeStore()

class Entity extends AppRecord {
  get controller_name(){
    return 'EntitiesController'
  }
  get investments(){
    return employeeStore.investments.filter(inv => this.deep_descendant_ids.includes(inv.entity_id))
  }
  get descendant_ids(){
    return this.getChildren(this.id)
  }
  getChildren(parent_id){
    return employeeStore.entity_relations.filter(rel => rel.parent_id === parent_id).map(el => el.child_id)
  }
  get descendants(){
    return employeeStore.entities.filter(ent => this.descendant_ids.includes(ent.id))
  }
  get deep_descendants(){
    let ids = this.deep_ascendant_ids
    return employeeStore.entities.filter(ent => ids.includes(ent.id))
  }
  get deep_descendant_ids(){
    var allChildren = []
    var children = this.getChildren(this.id)
    while(children.length > 0){
      allChildren.push(children)
      allChildren = allChildren.flat(1)
      children = children.map(child => this.getChildren(child)).flat(1)
    }
    return [this.id].concat(allChildren)
  }
  get ascendant_ids(){
    return employeeStore.entity_relations.filter(rel => rel.child_id === this.id).map(el => el.parent_id)
  }
  get ascendants(){
    return employeeStore.entities.filter(ent => this.ascendant_ids.includes(ent.id))
  }
  get deep_ascendants(){
    return [this].concat(this.ascendants.map(el => el.deep_ascendants)).flat(Infinity)
  }
  get deep_ascendant_ids(){
    return this.deep_ascendants.map(desc => desc.id)
  }
  get ownerships(){
    return employeeStore.ownerships.filter(own => own.entity_id === this.id)
  }
  get people_ids(){
    return this.ownerships.map(own => own.person_id)
  }
  get people(){
    let ids = this.people_ids
    return employeeStore.people.filter(per => ids.includes(per.id))
  }
  get clients(){
    return this.people.filter(per => per.type === 'Client')
  }
  get owners(){
    return this.people.filter(per => (per.type === 'Client')||(per.type === 'Employee') )
  }
  get accounts(){
    return employeeStore.accounts.filter(acc => acc.entity_id === this.id)
  }
  get deep_accounts(){
    let ids = this.deep_descendant_ids
    return employeeStore.accounts.filter(acc => ids.includes(acc.entity_id))
  }
  get deep_deposits(){
    return employeeStore.deposits.filter(dep => this.deep_descendant_ids.includes(dep.entity_id))
  }
  get deposits(){
    return employeeStore.deposits.filter(dep => dep.entity_id === this.id)
  }
  get fee_rates(){
    return employeeStore.fee_rates.filter(fr => fr.entity_id === this.id)
  }
  get risk_assessments(){
    return employeeStore.risk_assessments.filter(risk => risk.entity_id === this.id)
  }
  set risk_assessments(items){
    this.setRelatedData(items, 'RiskAssessmentsController')
  }
  get current_risk_assessment(){
    return [...this.risk_assessments].sort((a, b) => {
      if (a.from > b.from) { return 1 }
      return -1
    })[0]
  }
  // Sort risk assessments by 'from' date in descending order, i.e., latest date first:
  get descending_sorted_risk_assessment() {
    return [...this.risk_assessments].sort((a, b) => {
      if (a.from < b.from) return 1;
      return -1;
    });
  }
  get first_descending_sorted_risk_assessment() {
    return this.descending_sorted_risk_assessment[0]
  }
  set accounts(items){
    this.setRelatedData(items, 'AccountsController')
  }
  set deposits(items){
    this.setRelatedData(items, 'DepositsController')
  }
  set fee_rates(items){
    this.setRelatedData(items, 'FeeRatesController')
  }
  get contact_informations(){
    return employeeStore.contact_informations.filter(cc => {
      return (cc.contactable_type === 'Entity') && (cc.contactable_id === this.id)
    })
  }
  get value_ytd(){
    return (this.total_invesment||{value_ytd: 0})['value_ytd']
  }
  get ret_ytd(){
    return (this.total_invesment||{ret_ytd: 0})['ret_ytd']
  }
  get rpct_ytd(){
    return (this.total_invesment||{rpct_ytd: 0})['rpct_ytd']
  }
  get value_prev(){
    return (this.total_invesment||{value_prev: 0})['value_prev']
  }
  get ret_prev(){
    return (this.total_invesment||{ret_prev: 0})['ret_prev']
  }
  get rpct_prev(){
    return (this.total_invesment||{rpct_prev: 0})['rpct_prev']
  }
  get allocation_limits(){
    return employeeStore.allocation_limits.filter(alloc => alloc.entity_id === this.id)
  }
  get active_allocation_limits(){
    return [...employeeStore.allocation_limits].filter(alloc => alloc.entity_id === this.id && alloc.active === true).sort((a, b) => {
      if (a.asset_class > b.asset_class) { return 1 }
      if (b.asset_class > a.asset_class) { return -1 }
      return 0
    })
  }
  get entity_allocations(){
    return [...employeeStore.entity_allocations].filter(alloc => alloc.entity_id === this.id).sort((a, b) => {
      if (a.from_date > b.from_date) { return 1 }
      return -1 
    })
  }
  get is_company(){
    const companies = ['operation', 'holding', 'fund']
    if (companies.includes(this.investment_type) || this.cvr){
      return true
    }

    return false
  }

  get entity_reporting_group(){
    return employeeStore.entity_reporting_groups.find(rep => rep.id === this.entity_reporting_group_id)
  }

  get invoice_type(){
    return employeeStore.invoice_types.find(it => it.id === this.invoice_type_id)
  }

  get invoice_type_name(){
    return (this.invoice_type||{}).name
  }

  get client_type_name(){
    return (employeeStore.client_type.find(clt => clt.key == this.client_type) || {}).label
  }

  get entity_invoice_types(){
    return employeeStore.entity_invoice_types.filter(eit => eit.entity_id == this.id)
  }

  get entity_vehicle_cost_estimates(){
    return employeeStore.entity_vehicle_cost_estimates.filter(eit => eit.entity_id == this.id)
  }

  get classification_type_formatted(){
    return (employeeStore.classification_type.find(x => x.key === this.classification_type) || {}).label
  }

  get withBM(){
    return this.entity_reporting_group && this.entity_reporting_group.with_bm
  }
}

export default Entity
